import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import type { BadgeTone } from '../components/ui';

/**
 * Remote work (operator, 2026-10-28): someone who works from the office asks to
 * work remotely for one day or more; a leader of their department approves it
 * first, then an administrator — two approvals, one after the other.
 */
export type RemoteWorkStatus = 'PENDING_LEADER' | 'PENDING_ADMIN' | 'APPROVED' | 'REJECTED' | 'CANCELLED';

export const REMOTE_WORK_STATUSES: RemoteWorkStatus[] = ['PENDING_LEADER', 'PENDING_ADMIN', 'APPROVED', 'REJECTED', 'CANCELLED'];

interface Person {
  id: string;
  fullName: string;
  fullNameAr: string | null;
}

export interface RemoteWork {
  id: string;
  userId: string;
  startDate: string;
  endDate: string;
  /** Working days in the range: holidays and rostered days off are not counted. */
  days: number;
  reason: string;
  status: RemoteWorkStatus;
  /** Nobody led the employee's departments when they asked: it went straight to an administrator. */
  leaderSkipped: boolean;
  leaderId: string | null;
  leaderDecidedAt: string | null;
  leaderNote: string | null;
  adminId: string | null;
  adminDecidedAt: string | null;
  adminNote: string | null;
  cancelledAt: string | null;
  createdAt: string;
  user: Person & { employeeCode: string | null };
  department: { id: string; name: string; nameAr: string | null } | null;
  leader: Person | null;
  admin: Person | null;
  cancelledBy: Person | null;
  /** The approval it waits for — the team leader's, then an administrator's — or null once settled. */
  waitingFor: 'LEADER' | 'ADMIN' | null;
  can: { decide: boolean; cancel: boolean };
}

export interface RemoteWorkDetail extends RemoteWork {
  /** While it waits for the team leader: who may give that approval. */
  teamLeaders: Person[];
}

/** `GET /remote-work/preview` — what the form shows before it is sent. */
export interface RemoteWorkPreview {
  days: number;
  overlapping: { id: string; status: RemoteWorkStatus; startDate: string; endDate: string } | null;
  /** Who gives the first approval. None: it goes straight to an administrator. */
  teamLeaders: Person[];
}

/** Waiting for someone, approved, or settled the other way. */
export function remoteWorkTone(status: RemoteWorkStatus): BadgeTone {
  switch (status) {
    case 'PENDING_LEADER':
    case 'PENDING_ADMIN':
      return 'warning';
    case 'APPROVED':
      return 'success';
    case 'REJECTED':
      return 'error';
    default:
      return 'neutral';
  }
}

/**
 * How many requests wait for the signed-in person's approval — as a team leader,
 * or as an administrator: the sidebar's count beside Remote work and the tab's.
 * A new one rings the bell too, so this keeps the bell's pace.
 */
export function useWaitingRemoteWork() {
  const { data } = useQuery({
    queryKey: ['remote-work', 'waiting-count'],
    queryFn: () => api.get<{ review: number; count: number }>('/remote-work/waiting-count'),
    refetchInterval: 30_000,
  });
  return data ?? { review: 0, count: 0 };
}
